// src/page/CreateDashboard.tsx
import React, { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { CreateDashboardModal } from "@metabase/embedding-sdk-react";
import { API_BASE } from "../services/apiBase.ts";
import { createSavedItem, getAdminTenantHeader } from "../services/saved.ts";

type Props = {
  onCreated: (dashboardId: number) => void;
  onClose?: () => void;
};

const CreateDashboard: React.FC<Props> = ({ onCreated, onClose }) => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const email = (auth.user?.profile as any)?.email ?? undefined;

  const [saveError, setSaveError] = useState<string | null>(null);
  const [collectionId, setCollectionId] = useState<number | null>(null);
  const [loadingCollection, setLoadingCollection] = useState(true);

  useEffect(() => {
    async function loadCollection() {
      if (!accessToken) {
        setLoadingCollection(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/collection-ids`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...(email ? { "X-User-Email": email } : {}),
            ...getAdminTenantHeader(),
          },
        });

        if (!res.ok) {
          throw new Error("Failed to load collection ids");
        }

        const data = await res.json();
        setCollectionId(data.saved_collection_id);
      } catch (err) {
        console.error("Failed to load collection id", err);
        setSaveError("Failed to load collection configuration");
      } finally {
        setLoadingCollection(false);
      }
    }

    loadCollection();
  }, [accessToken, email]);

  if (!accessToken) {
    return <div style={{ padding: 16 }}>Missing access token; please sign in again.</div>;
  }

  return (
    <div className="rt-mb-container">
      {loadingCollection ? (
        <div style={{ padding: 12, opacity: 0.8 }}>Loading dashboard builder...</div>
      ) : null}

      {!loadingCollection && collectionId == null ? (
        <div style={{ padding: 12, color: "#f87171" }}>
          No saved collection is configured for this tenant.
        </div>
      ) : null}

      <div style={{ display: loadingCollection || collectionId == null ? "none" : "block" }}>
        <CreateDashboardModal
          initialCollectionId={collectionId ?? undefined}
          onClose={() => onClose?.()}
          onCreate={async (dashboard: any) => {
            try {
              setSaveError(null);

              const id =
                typeof dashboard?.id === "number"
                  ? dashboard.id
                  : Number.isFinite(Number(dashboard?.id))
                  ? Number(dashboard.id)
                  : Number.isFinite(Number(dashboard?.entity_id))
                  ? Number(dashboard.entity_id)
                  : NaN;

              if (!Number.isFinite(id)) {
                throw new Error("Dashboard creation succeeded, but no dashboard id was returned.");
              }

              const title =
                dashboard?.name ||
                dashboard?.title ||
                `Dashboard ${id}`;

              if (collectionId == null) {
                throw new Error("No saved collection is configured for this tenant.");
              }

              const moveResp = await fetch(`${API_BASE}/api/saved/dashboard/${id}/collection`, {
                method: "PUT",
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  "Content-Type": "application/json",
                  ...(email ? { "X-User-Email": email } : {}),
                  ...getAdminTenantHeader(),
                },
              });

              if (!moveResp.ok) {
                const text = await moveResp.text();
                throw new Error(`Failed to move dashboard to saved collection: ${text}`);
              }

              await createSavedItem(
                accessToken,
                { kind: "dashboard", metabase_id: id, title },
                email,
              );

              onCreated(id);
            } catch (e: any) {
              setSaveError(e?.message || "Failed to save dashboard metadata");
            }
          }}
        />
      </div>

      {saveError ? (
        <div style={{ padding: 12, color: "#f87171" }}>
          {saveError}
        </div>
      ) : null}
    </div>
  );
};

export default CreateDashboard;
